// Binary tree algorithms — emit the same event stream consumed by the store.
// Trees use init-graph / set-node-state / set-edge-state / node-pointer / move-nodes.

const X_SPACING = 1.7
const Y_SPACING = 1.9

// Build a tree from a LeetCode-style level-order array (null = missing child).
// Returns the root node: { id, value, left, right }.
function buildFromLevelOrder(arr) {
  if (!arr.length || arr[0] == null) return null
  let idc = 0
  const make = (v) => ({ id: idc++, value: v, left: null, right: null })
  const root = make(arr[0])
  const queue = [root]
  let i = 1
  while (i < arr.length) {
    const node = queue.shift()
    if (i < arr.length) {
      if (arr[i] != null) { node.left = make(arr[i]); queue.push(node.left) }
      i++
    }
    if (i < arr.length) {
      if (arr[i] != null) { node.right = make(arr[i]); queue.push(node.right) }
      i++
    }
  }
  return root
}

// Assign x/y world positions via in-order index (x) and depth (y).
// Returns { nodes:[{id,value,x,y}], edges:[{from,to}] }.
function layout(root) {
  const pos = {}
  let order = 0
  let maxDepth = 0
  ;(function inorder(node, depth) {
    if (!node) return
    inorder(node.left, depth + 1)
    pos[node.id] = { order: order++, depth }
    maxDepth = Math.max(maxDepth, depth)
    inorder(node.right, depth + 1)
  })(root, 0)

  const maxOrder = order - 1
  const nodes = []
  const edges = []
  ;(function collect(node) {
    if (!node) return
    const p = pos[node.id]
    const x = (p.order - maxOrder / 2) * X_SPACING
    const y = (-p.depth + maxDepth / 2) * Y_SPACING
    nodes.push({ id: node.id, value: node.value, x, y })
    if (node.left)  { edges.push({ from: node.id, to: node.left.id }); collect(node.left) }
    if (node.right) { edges.push({ from: node.id, to: node.right.id }); collect(node.right) }
  })(root)

  return { nodes, edges }
}

// Recompute positions for the current structure (used by invert).
function positionsOf(root) {
  return layout(root).nodes.map(({ id, x, y }) => ({ id, x, y }))
}

const SAMPLE_TREE = [4, 2, 7, 1, 3, 6, 9]
const SAMPLE_BST = [8, 3, 10, 1, 6, 9, 14, null, null, 4, 7]

// ─── DFS (pre-order traversal) ───────────────────────────────────────────────
export function treeDFS() {
  const root = buildFromLevelOrder(SAMPLE_TREE)
  const { nodes, edges } = layout(root)
  const steps = []
  steps.push({
    type: 'init-graph', nodes, edges,
    operation: 'Initialize',
    explanation: 'Depth-First Search (pre-order): visit a node, then its left subtree, then its right subtree.',
  })

  const order = []
  function dfs(node, parent, dir) {
    if (!node) return
    steps.push({ type: 'recurse-enter', label: `dfs(${node.value})`, nodeId: node.id })
    if (parent != null) {
      steps.push({ type: 'set-edge-state', edges: [[parent, node.id]], state: 'active',
        explanation: `Descend ${dir} from parent` })
    }
    steps.push({ type: 'node-pointer', name: 'cur', id: node.id })
    steps.push({ type: 'set-node-state', ids: [node.id], state: 'active',
      operation: `Visit ${node.value}`, explanation: `Process node ${node.value}` })
    order.push(node.value)
    steps.push({ type: 'vars', vars: { order: `[${order.join(', ')}]` } })
    dfs(node.left, node.id, 'left')
    dfs(node.right, node.id, 'right')
    steps.push({ type: 'set-node-state', ids: [node.id], state: 'visited',
      explanation: `Done with subtree rooted at ${node.value}` })
    steps.push({ type: 'recurse-exit' })
  }
  dfs(root, null)
  steps.push({ type: 'node-pointer', name: 'cur', remove: true })
  steps.push({ type: 'set-node-state', ids: nodes.map((n) => n.id), state: 'answer',
    operation: 'Traversal complete', explanation: `Pre-order DFS visited: ${order.join(' → ')}` })
  return steps
}

// ─── BFS (level-order traversal) ─────────────────────────────────────────────
export function treeBFS() {
  const root = buildFromLevelOrder(SAMPLE_TREE)
  const { nodes, edges } = layout(root)
  const steps = []
  steps.push({
    type: 'init-graph', nodes, edges,
    operation: 'Initialize',
    explanation: 'Level-order traversal (BFS): process the tree one depth level at a time using a queue.',
  })

  const order = []
  const queue = [root]
  let level = 0
  while (queue.length) {
    const levelSize = queue.length
    const levelVals = []
    steps.push({ type: 'vars', vars: { level, 'queue size': levelSize },
      operation: `Level ${level}`, explanation: `Process all ${levelSize} node(s) at depth ${level}` })
    for (let k = 0; k < levelSize; k++) {
      const node = queue.shift()
      steps.push({ type: 'node-pointer', name: 'cur', id: node.id })
      steps.push({ type: 'set-node-state', ids: [node.id], state: 'active',
        explanation: `Dequeue ${node.value}` })
      order.push(node.value)
      levelVals.push(node.value)
      if (node.left)  { steps.push({ type: 'set-edge-state', edges: [[node.id, node.left.id]], state: 'active' }); queue.push(node.left) }
      if (node.right) { steps.push({ type: 'set-edge-state', edges: [[node.id, node.right.id]], state: 'active' }); queue.push(node.right) }
      steps.push({ type: 'set-node-state', ids: [node.id], state: 'visited' })
    }
    steps.push({ type: 'vars', vars: { order: `[${order.join(', ')}]` } })
    level++
  }
  steps.push({ type: 'node-pointer', name: 'cur', remove: true })
  steps.push({ type: 'set-node-state', ids: nodes.map((n) => n.id), state: 'answer',
    operation: 'Traversal complete', explanation: `Level-order: ${order.join(' → ')}` })
  return steps
}

// ─── Invert Binary Tree ──────────────────────────────────────────────────────
export function invertTree() {
  const root = buildFromLevelOrder(SAMPLE_TREE)
  const { nodes, edges } = layout(root)
  const steps = []
  steps.push({
    type: 'init-graph', nodes, edges,
    operation: 'Initialize',
    explanation: 'Invert a binary tree: at every node, swap its left and right children (mirror the tree).',
  })

  // BFS order so the mirror reads top-down
  const queue = [root]
  while (queue.length) {
    const node = queue.shift()
    steps.push({ type: 'node-pointer', name: 'cur', id: node.id })
    steps.push({ type: 'set-node-state', ids: [node.id], state: 'active',
      operation: `Swap children of ${node.value}`,
      explanation: node.left || node.right
        ? `Swap the left and right subtrees of node ${node.value}`
        : `Node ${node.value} is a leaf — nothing to swap` })

    if (node.left || node.right) {
      const childIds = [node.left?.id, node.right?.id].filter((x) => x != null)
      steps.push({ type: 'set-node-state', ids: childIds, state: 'compared' })
      // perform swap
      ;[node.left, node.right] = [node.right, node.left]
      // animate to new layout
      steps.push({ type: 'move-nodes', positions: positionsOf(root),
        explanation: `Children of ${node.value} swapped` })
      steps.push({ type: 'set-node-state', ids: childIds, state: 'inactive' })
    }
    steps.push({ type: 'set-node-state', ids: [node.id], state: 'visited' })
    if (node.left) queue.push(node.left)
    if (node.right) queue.push(node.right)
  }
  steps.push({ type: 'node-pointer', name: 'cur', remove: true })
  steps.push({ type: 'set-node-state', ids: nodes.map((n) => n.id), state: 'answer',
    operation: 'Inverted', explanation: 'The tree is now fully mirrored.' })
  return steps
}

// ─── Validate Binary Search Tree ─────────────────────────────────────────────
export function validateBST() {
  const root = buildFromLevelOrder(SAMPLE_BST)
  const { nodes, edges } = layout(root)
  const steps = []
  steps.push({
    type: 'init-graph', nodes, edges,
    operation: 'Initialize',
    explanation: 'Validate a BST: an in-order traversal must produce strictly increasing values.',
  })

  let prev = null
  let valid = true
  const seen = []
  function inorder(node) {
    if (!node || !valid) return
    steps.push({ type: 'recurse-enter', label: `inorder(${node.value})`, nodeId: node.id })
    inorder(node.left)
    if (!valid) { steps.push({ type: 'recurse-exit' }); return }
    steps.push({ type: 'node-pointer', name: 'cur', id: node.id })
    steps.push({ type: 'set-node-state', ids: [node.id], state: 'active',
      operation: `Check ${node.value}`,
      explanation: prev == null
        ? `First in-order value is ${node.value}`
        : `Is ${node.value} > previous (${prev})?` })
    if (prev != null && node.value <= prev) {
      steps.push({ type: 'set-node-state', ids: [node.id], state: 'rejected',
        operation: 'Not a BST', explanation: `${node.value} ≤ ${prev} — in-order not increasing ✗` })
      valid = false
      steps.push({ type: 'recurse-exit' })
      return
    }
    seen.push(node.value)
    steps.push({ type: 'vars', vars: { prev: node.value, 'in-order': `[${seen.join(', ')}]` } })
    steps.push({ type: 'set-node-state', ids: [node.id], state: 'visited' })
    prev = node.value
    inorder(node.right)
    steps.push({ type: 'recurse-exit' })
  }
  inorder(root)

  if (valid) {
    steps.push({ type: 'node-pointer', name: 'cur', remove: true })
    steps.push({ type: 'set-node-state', ids: nodes.map((n) => n.id), state: 'answer',
      operation: 'Valid BST ✓', explanation: `In-order ${seen.join(' < ')} is strictly increasing.` })
  }
  return steps
}

// ─── Maximum Depth of Binary Tree (#104) ─────────────────────────────────────
export function maxDepthTree() {
  const root = buildFromLevelOrder([3, 9, 20, null, null, 15, 7])
  const { nodes, edges } = layout(root)
  const steps = []
  steps.push({
    type: 'init-graph', nodes, edges,
    operation: 'Initialize',
    explanation: 'Maximum Depth: DFS returning height at each node. depth(node) = max(depth(left), depth(right)) + 1.',
  })

  function dfs(node) {
    if (!node) return 0
    steps.push({ type: 'recurse-enter', label: `depth(${node.value})`, nodeId: node.id })
    steps.push({ type: 'node-pointer', name: 'cur', id: node.id })
    steps.push({ type: 'set-node-state', ids: [node.id], state: 'active',
      operation: `depth(${node.value})`, explanation: `Recurse into subtrees of ${node.value}` })
    const l = dfs(node.left)
    const r = dfs(node.right)
    const d = Math.max(l, r) + 1
    steps.push({ type: 'vars', vars: { [`depth(${node.value})`]: d },
      explanation: `max(left=${l}, right=${r}) + 1 = ${d}` })
    steps.push({ type: 'set-node-state', ids: [node.id], state: 'visited' })
    steps.push({ type: 'recurse-exit' })
    return d
  }

  const result = dfs(root)
  steps.push({ type: 'node-pointer', name: 'cur', remove: true })
  steps.push({ type: 'set-node-state', ids: nodes.map((n) => n.id), state: 'answer',
    operation: `Max Depth = ${result}`,
    explanation: `The tree has ${result} levels from root to the deepest leaf.` })
  return steps
}

// ─── Balanced Binary Tree (#110) ─────────────────────────────────────────────
export function balancedTree() {
  const root = buildFromLevelOrder([3, 9, 20, null, null, 15, 7])
  const { nodes, edges } = layout(root)
  const steps = []
  steps.push({
    type: 'init-graph', nodes, edges,
    operation: 'Initialize',
    explanation: 'Balanced Binary Tree: every node\'s left and right subtrees differ in height by at most 1.',
  })

  let balanced = true

  function height(node) {
    if (!node) return 0
    steps.push({ type: 'recurse-enter', label: `h(${node.value})`, nodeId: node.id })
    steps.push({ type: 'node-pointer', name: 'cur', id: node.id })
    steps.push({ type: 'set-node-state', ids: [node.id], state: 'active',
      explanation: `Check balance at node ${node.value}` })
    const l = height(node.left)
    const r = height(node.right)
    const diff = Math.abs(l - r)
    const h = Math.max(l, r) + 1
    if (diff > 1) {
      balanced = false
      steps.push({ type: 'set-node-state', ids: [node.id], state: 'rejected',
        operation: 'Unbalanced!', explanation: `|h(left)=${l} − h(right)=${r}| = ${diff} > 1 — unbalanced ✗` })
    } else {
      steps.push({ type: 'vars', vars: { [`h(${node.value})`]: h, diff },
        explanation: `|h(left)=${l} − h(right)=${r}| = ${diff} ≤ 1 ✓` })
      steps.push({ type: 'set-node-state', ids: [node.id], state: 'visited' })
    }
    steps.push({ type: 'recurse-exit' })
    return h
  }

  height(root)
  steps.push({ type: 'node-pointer', name: 'cur', remove: true })
  steps.push({ type: 'set-node-state', ids: nodes.map((n) => n.id), state: balanced ? 'answer' : 'rejected',
    operation: balanced ? 'Balanced ✓' : 'Not Balanced ✗',
    explanation: balanced
      ? 'All nodes have height-balanced subtrees.'
      : 'At least one node has unbalanced subtrees.' })
  return steps
}

// ─── Binary Tree Right Side View (#199) ──────────────────────────────────────
export function rightSideView() {
  const root = buildFromLevelOrder([1, 2, 3, null, 5, null, 4])
  const { nodes, edges } = layout(root)
  const steps = []
  steps.push({ type: 'init-graph', nodes, edges, operation: 'Initialize', explanation: 'Right side view: BFS level-order, take the last node at each level.' })

  const result = []
  const queue = [root]
  let level = 0
  while (queue.length) {
    const size = queue.length
    let rightmost = null
    steps.push({ type: 'vars', vars: { level }, operation: `Level ${level}`, explanation: `Process ${size} node(s) at depth ${level}` })
    for (let k = 0; k < size; k++) {
      const node = queue.shift()
      steps.push({ type: 'node-pointer', name: 'cur', id: node.id })
      steps.push({ type: 'set-node-state', ids: [node.id], state: k === size - 1 ? 'active' : 'compared', explanation: k === size - 1 ? `Last node at level ${level} — visible from right!` : `Not rightmost at level ${level}` })
      rightmost = node
      if (node.left) queue.push(node.left)
      if (node.right) queue.push(node.right)
    }
    result.push(rightmost.value)
    steps.push({ type: 'set-node-state', ids: [rightmost.id], state: 'answer', explanation: `${rightmost.value} is rightmost at level ${level}` })
    steps.push({ type: 'vars', vars: { result: `[${result.join(', ')}]` } })
    level++
  }
  steps.push({ type: 'node-pointer', name: 'cur', remove: true })
  steps.push({ operation: 'Right Side View ✓', explanation: `Visible from right: [${result.join(', ')}]` })
  return steps
}

// ─── Kth Smallest in BST (#230) ──────────────────────────────────────────────
export function kthSmallestBST(k = 3) {
  const root = buildFromLevelOrder(SAMPLE_BST)
  const { nodes, edges } = layout(root)
  const steps = []
  steps.push({ type: 'init-graph', nodes, edges, operation: 'Initialize', explanation: `Find the ${k}th smallest element in BST. In-order traversal gives sorted order.` })

  let count = 0, result = null
  const seen = []
  function inorder(node) {
    if (!node || result !== null) return
    steps.push({ type: 'recurse-enter', label: `inorder(${node.value})`, nodeId: node.id })
    inorder(node.left)
    if (result !== null) { steps.push({ type: 'recurse-exit' }); return }
    count++
    seen.push(node.value)
    steps.push({ type: 'node-pointer', name: 'cur', id: node.id })
    steps.push({ type: 'set-node-state', ids: [node.id], state: count === k ? 'answer' : 'visited', operation: `count=${count}`, explanation: count === k ? `count=${count} === k=${k} — found! ${node.value} is the ${k}th smallest ✓` : `In-order #${count}: ${node.value}` })
    steps.push({ type: 'vars', vars: { count, k, inOrder: `[${seen.join(', ')}]` } })
    if (count === k) { result = node.value; steps.push({ type: 'recurse-exit' }); return }
    inorder(node.right)
    steps.push({ type: 'recurse-exit' })
  }
  inorder(root)
  steps.push({ type: 'node-pointer', name: 'cur', remove: true })
  steps.push({ operation: `${k}th Smallest = ${result}`, explanation: `The ${k}th smallest value in the BST is ${result}.` })
  return steps
}

// ─── Lowest Common Ancestor of BST (#235) ────────────────────────────────────
export function lcaBST(pVal = 4, qVal = 7) {
  const root = buildFromLevelOrder(SAMPLE_BST)
  const { nodes, edges } = layout(root)
  const steps = []
  steps.push({ type: 'init-graph', nodes, edges, operation: 'Initialize', explanation: `LCA of BST: find LCA of nodes ${pVal} and ${qVal}. Use BST property to navigate.` })
  setNodeVars(steps, { p: pVal, q: qVal })

  function setNodeVars(s, v) { s.push({ type: 'vars', vars: v }) }

  let node = root
  while (node) {
    steps.push({ type: 'node-pointer', name: 'cur', id: node.id })
    steps.push({ type: 'set-node-state', ids: [node.id], state: 'active', operation: `Visit ${node.value}`, explanation: `Both ${pVal} and ${qVal} — are they on the same side?` })
    if (pVal < node.value && qVal < node.value) {
      steps.push({ type: 'set-node-state', ids: [node.id], state: 'compared', explanation: `Both ${pVal} and ${qVal} < ${node.value} — LCA is in left subtree` })
      node = node.left
    } else if (pVal > node.value && qVal > node.value) {
      steps.push({ type: 'set-node-state', ids: [node.id], state: 'compared', explanation: `Both ${pVal} and ${qVal} > ${node.value} — LCA is in right subtree` })
      node = node.right
    } else {
      steps.push({ type: 'set-node-state', ids: [node.id], state: 'answer', operation: `LCA = ${node.value}`, explanation: `${pVal} and ${qVal} split here — ${node.value} is their LCA ✓` })
      break
    }
  }
  steps.push({ type: 'node-pointer', name: 'cur', remove: true })
  return steps
}

// ─── Lowest Common Ancestor of Binary Tree (#236) ────────────────────────────
export function lcaBinaryTree(pVal = 5, qVal = 1) {
  const root = buildFromLevelOrder([3, 5, 1, 6, 2, 0, 8, null, null, 7, 4])
  const { nodes, edges } = layout(root)
  const steps = []
  steps.push({ type: 'init-graph', nodes, edges, operation: 'Initialize', explanation: `LCA of Binary Tree: find LCA of ${pVal} and ${qVal} via post-order DFS.` })

  let lcaId = null
  function dfs(node) {
    if (!node) return false
    steps.push({ type: 'recurse-enter', label: `dfs(${node.value})`, nodeId: node.id })
    steps.push({ type: 'set-node-state', ids: [node.id], state: 'active', explanation: `Check if ${node.value} is on path to ${pVal} or ${qVal}` })
    const left = dfs(node.left)
    const right = dfs(node.right)
    const isSelf = node.value === pVal || node.value === qVal
    const found = (left ? 1 : 0) + (right ? 1 : 0) + (isSelf ? 1 : 0) >= 2
    if (found && lcaId === null) {
      lcaId = node.id
      steps.push({ type: 'set-node-state', ids: [node.id], state: 'answer', operation: `LCA = ${node.value}`, explanation: `${node.value} is the LCA of ${pVal} and ${qVal} ✓` })
    } else if (isSelf || left || right) {
      steps.push({ type: 'set-node-state', ids: [node.id], state: 'visited', explanation: `${node.value} is on the path` })
    }
    steps.push({ type: 'recurse-exit' })
    return isSelf || left || right
  }
  dfs(root)
  steps.push({ type: 'node-pointer', name: 'cur', remove: true })
  return steps
}

// ─── Construct Binary Tree from Preorder and Inorder (#105) ──────────────────
export function constructBinaryTree() {
  const preorder = [3, 9, 20, 15, 7]
  const inorder  = [9, 3, 15, 20, 7]
  const steps = []

  let nodeId = 0
  function build(pre, ino, depth) {
    if (!pre.length) return null
    const rootVal = pre[0]
    const mid = ino.indexOf(rootVal)
    const node = { id: nodeId++, value: rootVal, left: null, right: null }
    steps.push({ type: 'vars', vars: { rootVal, inorderLeft: `[${ino.slice(0, mid).join(',')}]`, inorderRight: `[${ino.slice(mid+1).join(',')}]` }, operation: `Build node ${rootVal}`, explanation: `preorder[0]=${rootVal} is root. In inorder, it splits at index ${mid}.` })
    node.left  = build(pre.slice(1, mid + 1), ino.slice(0, mid), depth + 1)
    node.right = build(pre.slice(mid + 1),    ino.slice(mid + 1), depth + 1)
    return node
  }

  // Start with empty tree init, build structure first
  const root = build(preorder, inorder, 0)
  const { nodes, edges } = layout(root)
  steps.unshift({ type: 'init-graph', nodes, edges, operation: 'Initialize', explanation: `Construct tree from preorder [${preorder.join(',')}] and inorder [${inorder.join(',')}].` })

  // Final highlight
  steps.push({ type: 'set-node-state', ids: nodes.map(n => n.id), state: 'answer', operation: 'Tree Built ✓', explanation: `Tree constructed successfully from preorder and inorder traversals.` })
  return steps
}

// ─── Diameter of Binary Tree (#543) ──────────────────────────────────────────
export function diameterTree() {
  const root = buildFromLevelOrder([1, 2, 3, 4, 5])
  const { nodes, edges } = layout(root)
  const steps = []
  steps.push({
    type: 'init-graph', nodes, edges,
    operation: 'Initialize',
    explanation: 'Diameter: longest path between any two nodes. At each node: diameter = depth(left) + depth(right).',
  })

  let maxDia = 0

  function depth(node) {
    if (!node) return 0
    steps.push({ type: 'recurse-enter', label: `d(${node.value})`, nodeId: node.id })
    steps.push({ type: 'node-pointer', name: 'cur', id: node.id })
    steps.push({ type: 'set-node-state', ids: [node.id], state: 'active',
      explanation: `Compute subtree depth at node ${node.value}` })
    const l = depth(node.left)
    const r = depth(node.right)
    const dia = l + r
    if (dia > maxDia) maxDia = dia
    steps.push({ type: 'vars', vars: { [`path@${node.value}`]: `${l}+${r}=${dia}`, maxDia },
      explanation: `Path through ${node.value}: left depth ${l} + right depth ${r} = ${dia}. Max so far: ${maxDia}` })
    steps.push({ type: 'set-node-state', ids: [node.id], state: 'visited' })
    steps.push({ type: 'recurse-exit' })
    return Math.max(l, r) + 1
  }

  depth(root)
  steps.push({ type: 'node-pointer', name: 'cur', remove: true })
  steps.push({ type: 'set-node-state', ids: nodes.map((n) => n.id), state: 'answer',
    operation: `Diameter = ${maxDia}`,
    explanation: `The longest path between any two nodes has ${maxDia} edges.` })
  return steps
}
